import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Wrench, User as UserIcon, CheckCircle2, Send, RotateCcw, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime as formatDate } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: 'Pending — with Management', color: 'bg-yellow-100 text-yellow-700' },
  assigned: { label: 'Assigned — with Supervisor', color: 'bg-blue-100 text-blue-700' },
  completed_by_supervisor: { label: 'Completed — Awaiting Management Review', color: 'bg-indigo-100 text-indigo-700' },
  sent_to_resident: { label: 'Awaiting Resident Confirmation', color: 'bg-purple-100 text-purple-700' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700' },
};


export const Maintenance = () => {
  const { user } = useAuth();
  const isResident = user?.role === 'resident';
  const isManagement = user?.role === 'management';
  const isSupervisor = user?.role === 'supervisor';

  const [requests, setRequests] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', category: 'plumbing', unit_id: user?.unit_number || '',
  });

  // Assign modal (management)
  const [assignFor, setAssignFor] = useState(null); // request object
  const [assignData, setAssignData] = useState({ supervisor_id: '', note: '' });

  // Complete modal (supervisor)
  const [completeFor, setCompleteFor] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');

  // Forward-to-resident modal (management)
  const [forwardFor, setForwardFor] = useState(null);
  const [managementNotes, setManagementNotes] = useState('');

  useEffect(() => {
    loadRequests();
    if (isManagement) loadSupervisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagement]);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/maintenance/requests`, { withCredentials: true });
      setRequests(data);
    } catch (err) {
      console.error('Maintenance: failed to load requests', err);
      toast.error('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSupervisors = useCallback(async (propertyId = null) => {
    try {
      const params = propertyId ? { property_id: propertyId } : {};
      const { data } = await axios.get(`${API}/maintenance/supervisors`, { params, withCredentials: true });
      setSupervisors(data);
    } catch (err) {
      // Non-critical: assign modal will show "No supervisors yet" hint.
      console.error('Maintenance: failed to load supervisors', err);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/maintenance/requests`, formData, { withCredentials: true });
      toast.success('Maintenance request submitted');
      setShowForm(false);
      setFormData({ title: '', description: '', category: 'plumbing', unit_id: user?.unit_number || '' });
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const openAssign = (req) => {
    setAssignFor(req);
    setAssignData({ supervisor_id: req.supervisor_id || '', note: '' });
    // Load supervisors filtered by this ticket's property
    if (isManagement && req.property_id) {
      loadSupervisors(req.property_id);
    }
  };
  const submitAssign = async () => {
    if (!assignData.supervisor_id) { toast.error('Select a supervisor'); return; }
    try {
      await axios.patch(`${API}/maintenance/requests/${assignFor._id}/assign`, assignData, { withCredentials: true });
      toast.success('Assigned to supervisor');
      setAssignFor(null);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const openComplete = (req) => { setCompleteFor(req); setCompletionNotes(''); };
  const submitComplete = async () => {
    if (!completionNotes.trim()) { toast.error('Please describe the work done'); return; }
    try {
      await axios.patch(`${API}/maintenance/requests/${completeFor._id}/complete`, { completion_notes: completionNotes }, { withCredentials: true });
      toast.success('Marked as completed — management will review');
      setCompleteFor(null);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    }
  };

  const openForward = (req) => { setForwardFor(req); setManagementNotes(''); };
  const submitForward = async () => {
    try {
      await axios.patch(`${API}/maintenance/requests/${forwardFor._id}/forward`, { management_notes: managementNotes }, { withCredentials: true });
      toast.success('Forwarded to resident');
      setForwardFor(null);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to forward');
    }
  };

  const reopenRequest = async (req) => {
    const reason = window.prompt('Why does this need rework? (visible to the supervisor)', '');
    if (!reason || !reason.trim()) return;
    try {
      await axios.patch(`${API}/maintenance/requests/${req._id}/reopen`, { reason }, { withCredentials: true });
      toast.success('Sent back to supervisor for rework');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen');
    }
  };

  const acknowledgeRequest = async (req) => {
    const isForwarded = req.status === 'sent_to_resident';
    const msg = isForwarded
      ? 'Confirm the work is complete and close this ticket?'
      : 'This will withdraw and close your request. Continue?';
    if (!window.confirm(msg)) return;
    try {
      await axios.patch(`${API}/maintenance/requests/${req._id}/acknowledge`, {}, { withCredentials: true });
      toast.success(isForwarded ? 'Ticket closed. Thank you!' : 'Ticket withdrawn');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close ticket');
    }
  };

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Maintenance Requests</h1>
          <p className="text-sm text-[#64748b]">
            {isResident && 'Submit and track your maintenance requests'}
            {isManagement && 'Triage, assign supervisors, review, and close tickets'}
            {isSupervisor && 'Tickets assigned to you — mark them complete when done'}
          </p>
        </div>
        {isResident && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="new-request-button">
            <Wrench className="mr-2 h-4 w-4" />
            {showForm ? 'Close' : 'New Request'}
          </Button>
        )}
      </div>

      {isResident && showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="maintenance-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Leaking faucet" required data-testid="maintenance-title-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Category</Label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white" data-testid="maintenance-category-select">
                <option value="plumbing">Plumbing</option>
                <option value="electrical">Electrical</option>
                <option value="hvac">HVAC</option>
                <option value="appliance">Appliance</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Description *</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Describe the issue in detail..." required rows={4} data-testid="maintenance-description-input" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-request-button">Submit Request</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Requests list */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto" /></div>
        ) : requests.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
            <Wrench className="h-12 w-12 text-[#cbd5e1] mx-auto mb-4" />
            <p className="text-[#64748b]">No maintenance requests {isResident ? 'yet' : 'to show'}</p>
          </Card>
        ) : (
          requests.map((r, index) => {
            const meta = STATUS_META[r.status] || { label: r.status, color: 'bg-gray-100 text-gray-700' };
            return (
              <Card key={r._id || index} className="p-6 bg-white border border-[#e2e8f0]" data-testid={`request-card-${index}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }} data-testid={`request-title-${index}`}>{r.title}</h3>
                    <p className="text-sm text-[#334155] mt-1 mb-3 whitespace-pre-wrap">{r.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-[#f1f5f9] text-xs font-medium rounded-full capitalize">{r.category}</span>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${meta.color}`} data-testid={`request-status-${index}`}>{meta.label}</span>
                      {r.unit_number && <span className="px-3 py-1 bg-[#f1f5f9] text-xs font-medium rounded-full">Unit {r.unit_number}</span>}
                      {r.supervisor_name && (
                        <span className="px-3 py-1 bg-cyan-50 text-cyan-700 text-xs font-medium rounded-full">
                          <UserIcon className="inline h-3 w-3 mr-1" />Supervisor: {r.supervisor_name}
                        </span>
                      )}
                    </div>

                    {/* Completion note (supervisor's work summary) */}
                    {r.completion_notes && (r.status === 'completed_by_supervisor' || r.status === 'sent_to_resident' || r.status === 'resolved') && (
                      <div className="mt-3 p-3 bg-indigo-50 border border-indigo-100 rounded-md text-sm">
                        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700 mb-1">Work performed by {r.completed_by}</p>
                        <p className="text-[#334155] whitespace-pre-wrap">{r.completion_notes}</p>
                      </div>
                    )}
                    {r.management_notes && (r.status === 'sent_to_resident' || r.status === 'resolved') && (
                      <div className="mt-2 p-3 bg-purple-50 border border-purple-100 rounded-md text-sm">
                        <p className="text-xs font-semibold uppercase tracking-widest text-purple-700 mb-1">Management note</p>
                        <p className="text-[#334155] whitespace-pre-wrap">{r.management_notes}</p>
                      </div>
                    )}
                    {r.reopen_reason && r.status === 'assigned' && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-md text-sm">
                        <p className="text-xs font-semibold uppercase tracking-widest text-red-700 mb-1">Reopened — please rework</p>
                        <p className="text-[#334155] whitespace-pre-wrap">{r.reopen_reason}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2 flex-shrink-0 text-right">
                    <p className="text-xs text-[#64748b]">Submitted {formatDate(r.created_at)}</p>
                    {r.user_name && <p className="text-xs text-[#64748b]">By {r.user_name}</p>}

                    {/* Action buttons per role + state */}
                    {isManagement && r.status === 'pending' && (
                      <Button size="sm" onClick={() => openAssign(r)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`assign-request-${index}`}>
                        <Send className="mr-1 h-4 w-4" /> Assign Supervisor
                      </Button>
                    )}
                    {isManagement && r.status === 'assigned' && (
                      <Button size="sm" variant="outline" onClick={() => openAssign(r)} data-testid={`reassign-request-${index}`}>
                        Reassign
                      </Button>
                    )}
                    {isManagement && r.status === 'completed_by_supervisor' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => reopenRequest(r)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reopen-request-${index}`}>
                          <RotateCcw className="mr-1 h-4 w-4" /> Rework
                        </Button>
                        <Button size="sm" onClick={() => openForward(r)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`forward-request-${index}`}>
                          <Send className="mr-1 h-4 w-4" /> Forward to Resident
                        </Button>
                      </div>
                    )}
                    {isSupervisor && r.status === 'assigned' && (
                      <Button size="sm" onClick={() => openComplete(r)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`complete-request-${index}`}>
                        <ClipboardCheck className="mr-1 h-4 w-4" /> Mark Complete
                      </Button>
                    )}
                    {isResident && r.status === 'sent_to_resident' && (
                      <Button size="sm" onClick={() => acknowledgeRequest(r)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`acknowledge-request-${index}`}>
                        <CheckCircle2 className="mr-1 h-4 w-4" /> Confirm & Close
                      </Button>
                    )}
                    {isResident && r.status !== 'resolved' && r.status !== 'sent_to_resident' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => acknowledgeRequest(r)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`withdraw-request-${index}`}
                      >
                        <CheckCircle2 className="mr-1 h-4 w-4" /> Close / Withdraw
                      </Button>
                    )}
                  </div>
                </div>

                {/* Timeline (compact) */}
                {r.history && r.history.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-[#e2e8f0]">
                    <p className="text-[10px] uppercase tracking-widest text-[#94a3b8] mb-2">Activity</p>
                    <ol className="space-y-1 text-xs text-[#64748b]" data-testid={`request-history-${index}`}>
                      {r.history.map((h, i) => (
                        <li key={`${r._id}-${h.at || i}-${h.action}`} className="flex flex-wrap gap-2">
                          <span className="text-[#0f172a] font-medium">{h.action.replace(/_/g, ' ')}</span>
                          <span>· by {h.by} ({h.role})</span>
                          <span>· {formatDate(h.at)}</span>
                          {h.supervisor_name && <span>· → {h.supervisor_name}</span>}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Assign supervisor modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAssignFor(null)}>
          <Card className="p-6 bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Assign Supervisor</h3>
            <p className="text-xs text-[#64748b] mb-4">{assignFor.title} · Unit {assignFor.unit_number || '—'}</p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Supervisor *</Label>
                <select value={assignData.supervisor_id} onChange={(e) => setAssignData({ ...assignData, supervisor_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white" data-testid="assign-supervisor-select">
                  <option value="">Select supervisor...</option>
                  {supervisors.map(s => (
                    <option key={s._id} value={s._id}>{s.name} — {s.email}</option>
                  ))}
                </select>
                {supervisors.length === 0 && (
                  <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-2">No supervisors yet — create one from User Management first.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Note to supervisor (optional)</Label>
                <Textarea value={assignData.note} onChange={(e) => setAssignData({ ...assignData, note: e.target.value })} rows={3} placeholder="e.g., Please attend within 24 hours." data-testid="assign-note-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[#e2e8f0]">
              <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
              <Button onClick={submitAssign} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-assign-button">Assign</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Supervisor complete modal */}
      {completeFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setCompleteFor(null)}>
          <Card className="p-6 bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="complete-modal">
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Mark Work Complete</h3>
            <p className="text-xs text-[#64748b] mb-4">{completeFor.title} · Unit {completeFor.unit_number || '—'}</p>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">What did you do? *</Label>
              <Textarea value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} rows={4} placeholder="Describe the work performed, parts replaced, etc." data-testid="completion-notes-input" />
              <p className="text-xs text-[#64748b]">This will be sent to management for review, then to the resident.</p>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[#e2e8f0]">
              <Button variant="outline" onClick={() => setCompleteFor(null)}>Cancel</Button>
              <Button onClick={submitComplete} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="submit-complete-button">Mark Complete</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Forward-to-resident modal (management) */}
      {forwardFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setForwardFor(null)}>
          <Card className="p-6 bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="forward-modal">
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Forward to Resident</h3>
            <p className="text-xs text-[#64748b] mb-4">{forwardFor.title} · {forwardFor.user_name}</p>
            {forwardFor.completion_notes && (
              <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-md text-sm mb-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700 mb-1">Supervisor summary</p>
                <p className="text-[#334155] whitespace-pre-wrap">{forwardFor.completion_notes}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Note to resident (optional)</Label>
              <Textarea value={managementNotes} onChange={(e) => setManagementNotes(e.target.value)} rows={3} placeholder="e.g., Work verified. Please confirm the issue is resolved." data-testid="forward-note-input" />
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[#e2e8f0]">
              <Button variant="outline" onClick={() => setForwardFor(null)}>Cancel</Button>
              <Button onClick={submitForward} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-forward-button">Send to Resident</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
